import React from 'react';
import { FormattedMessage as OriginalFormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useAppSelector } from './store';
import { i18nActions } from '../_actions/i18n.actions';

// Re-export everything from react-intl unchanged. The named `FormattedMessage`
// export below intentionally shadows the star-exported one.
export * from 'react-intl';

type FormattedMessageProps = React.ComponentProps<typeof OriginalFormattedMessage>;

export const FormattedMessage: (props: FormattedMessageProps) => JSX.Element = (props) => {
    const dispatch = useDispatch();
    const editMode = useAppSelector(state => state.i18n.editMode);
    const messages = useAppSelector(state => state.i18n.messages) as { [key: string]: string };

    const id = props.id as string | undefined;
    const hasKey = !!id && !!messages && Object.prototype.hasOwnProperty.call(messages, id);

    // Never render a pencil for the label editor's own chrome keys (modal title,
    // save/cancel/error labels): editing them would re-target the open editor to
    // itself. They are always rendered while edit mode is on, so guard here.
    if (!editMode || !hasKey || (id && id.startsWith('bk.label_edit.'))) {
        return <OriginalFormattedMessage {...props} />;
    }

    const openEditor = (e: React.MouseEvent | React.KeyboardEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dispatch(i18nActions.setEditingLabelKey(id as string));
    };

    // The pencil is a role="button" span (not a native <button>) because message
    // hosts are frequently native <button>/<a> elements; a nested <button> would
    // be invalid interactive-in-interactive DOM.
    return (
        <span className="relative group">
            <OriginalFormattedMessage {...props} />
            <span
                role="button"
                tabIndex={0}
                title="Edit label"
                onClick={openEditor}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { openEditor(e); } }}
                className="hidden group-hover:inline-flex items-center justify-center absolute -top-3 -right-3 w-4 h-4 bg-bk-pink-200 text-white rounded-full shadow z-20 cursor-pointer"
            >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-2.5 h-2.5">
                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-8.5 8.5a2 2 0 01-.878.507l-3 .857a.5.5 0 01-.618-.618l.857-3a2 2 0 01.507-.878l8.5-8.5z" />
                </svg>
            </span>
        </span>
    );
};
